import {
	Button, CircularProgress, Paper, Radio, RadioGroup, Stack, Tooltip,
	Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, IconButton
} from "@mui/material";

import { getLocationsetsItems } from "api/layer";
import { ReactComponent as MapPinSvg } from "assets/icon_map_pin.svg";
import { ReactComponent as CreateIcon } from "assets/location_set_creat.svg";
import { ReactComponent as ImportIcon } from "assets/location_set_import.svg";
import { LeftPanel, RightPanel } from "helpers/constants";
import { AppAction, AppContext, } from "helpers/context";
import { getCheckedCounts, createDefaultColumnList, createDefaultFilterList } from "helpers/helpers";
import { useContext, useState, useEffect, } from "react";
import { MapContext } from "helpers/map";
import { useTranslation } from "react-i18next";
import styles from "./data-sets.module.scss";
import { DrawShapeSource, LocationData } from "helpers/models";
import * as turf from '@turf/turf';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import 'overlayscrollbars/styles/overlayscrollbars.css';

interface Props {
	searchText: string;
}

function DataSets(props: Props) {
	const [context, dispatch] = useContext(AppContext);
	const { AMap, map } = useContext(MapContext);
	const locationSets = context.locationSets;
	const [isLoading, setIsLoading] = useState(undefined);
	const [t] = useTranslation();
	const [data, setData] = useState<LocationData[]>(locationSets || []);
	const [active, setActive] = useState("createTime");
	const [direction, setDirection] = useState<{ [key: string]: "asc" | "desc"; }>({ name: "asc", createTime: "desc", itemCount: "asc" });

	const handleSort = (property: string) => {
		if (!data) {
			return;
		}

		if (!property) {
			return;
		}

		setActive(property);
		direction[property] = direction[property] === "asc" ? "desc" : "asc";
		setDirection({ ...direction });

		let arr = [...locationSets!];
		arr.sort((a: any, b: any) => {
			if (!a || !b) {
				return 0;
			}

			const aValue = a?.[property];
			const bValue = b?.[property];

			if (!aValue) {
				return direction[property] === "asc" ? -1 : 1;
			} else if (!bValue) {
				return direction[property] === "asc" ? 1 : -1;
			}

			if (direction[property] === "asc") {
				return String(aValue).localeCompare(String(bValue), 'zh-CN', { numeric: true })
			} else {
				return String(bValue).localeCompare(String(aValue), 'zh-CN', { numeric: true })
			}
		});

		setData([...arr]);
	};

	const getItems = async (locationSet: any, is: boolean = false) => {
		setIsLoading(locationSet.id);
		// 先切换到 Multiple 面板，避免在 await 前渲染时 currentMultipleLocations 已设置
		// 而 rightPanel 仍为 Shape，导致 LineAnalysed 闪现回退按钮
		dispatch({ type: AppAction.setRightPanel, payload: RightPanel.Multiple });
		dispatch({
			type: AppAction.setCurrentHiddenLocationsModal,
			payload: undefined
		})
		dispatch({ type: AppAction.setCurrentMultipleLocations, payload: locationSet });
		dispatch({
			type: AppAction.setLocationColumnList,
			payload: createDefaultColumnList()
		})
		dispatch({
			type: AppAction.setLocationsFilterInfo,
			payload: createDefaultFilterList()
		})
		dispatch({
			type: AppAction.setMultipleLocationsSearch,
			payload: ""
		})
		is && dispatch({ type: AppAction.setMultipleLocationsSetLoadings, payload: true });
		is && dispatch({ type: AppAction.setMultipleLocationsEdit, payload: true });
		let res = await getLocationsetsItems(locationSet.id);
		const unit = res.data.aggregationUnit;
		const hasItems = Boolean(res.data.items?.length);
		const isValidUnit = unit !== "numberOfLocations";

		let updatedLocationSets = locationSets!.map((location: any) => {
			if (location.id === locationSet.id) {
				let hiddenLocationsArr: any = [];
				location.items = res.data.items.map((item: any) => {
					if (isValidUnit && !item[unit]) {
						hiddenLocationsArr.push(item)
					}
					item.checked = true;
					return item;
				});
				if (hasItems && isValidUnit) {
					dispatch({
						type: AppAction.setCurrentHiddenLocationsModal,
						payload: {
							open: false,
							tableData: hiddenLocationsArr
						}
					})
				}
			}
			return location;
		});

		dispatch({ type: AppAction.setCurrentMultipleLocations, payload: res.data });
		setIsLoading(undefined);
		dispatch({ type: AppAction.setLocationSets, payload: updatedLocationSets });
		dispatch({ type: AppAction.setRightPanel, payload: RightPanel.Multiple });
		is && dispatch({ type: AppAction.setMultipleLocationsSetLoadings, payload: false });
		handleClickMyLocation(res.data)
	};

	const openDataPoints = async (locationSet: any) => {
		if (context.currentMultipleLocationsAnalysed && context.currentMultipleLocationsAnalysed.id === locationSet.id) {
			dispatch({ type: AppAction.setMultipleLocationsAnalysed, payload: true });
		} else if (context.currentMultipleLocationsAnalysed) {
			dispatch({ type: AppAction.setMultipleLocationsAnalysed, payload: false });
		}
		if (locationSet.items === null || locationSet.items.length === 0) {
			await getItems(locationSet, true);
		} else {
			if (context.currentMultipleLocations?.id !== locationSet.id) {
				dispatch({
					type: AppAction.setLocationColumnList,
					payload: createDefaultColumnList()
				})
				dispatch({
					type: AppAction.setLocationsFilterInfo,
					payload: createDefaultFilterList()
				})
				dispatch({
					type: AppAction.setMultipleLocationsSearch,
					payload: ""
				})
				handleClickMyLocation(locationSet);
				dispatch({ type: AppAction.setRightPanel, payload: RightPanel.Multiple });
			}
			dispatch({ type: AppAction.setCurrentMultipleLocations, payload: locationSet });
			const unit = locationSet.aggregationUnit;
			const hasItems = Boolean(locationSet.items?.length);
			const isValidUnit = unit !== "numberOfLocations";
			// 主逻辑判断
			if (hasItems && isValidUnit) {
				let hiddenLocationsArr = locationSet.items?.filter((item: any) => !item[unit]) || [];
				dispatch({
					type: AppAction.setCurrentHiddenLocationsModal,
					payload: {
						open: false,
						tableData: hiddenLocationsArr
					}
				})
			} else {
				dispatch({
					type: AppAction.setCurrentHiddenLocationsModal,
					payload: undefined
				})
			}
			dispatch({ type: AppAction.setMultipleLocationsEdit, payload: true });
		}

	};

	const handleClickMyLocation = (locationSet: any) => {
		if (locationSet && locationSet.items) {
			let bounds: any = [];
			locationSet.items.map((item: any) => {
				if (item.checked) {
					bounds.push([item.longitude, item.latitude]);
				}
			});
			if (bounds.length === 0) return
			let bbox: any = turf.bboxPolygon(turf.bbox(turf.multiPoint(bounds)));
			var myBounds = new AMap.Bounds([bbox.bbox[2], bbox.bbox[3]], [bbox.bbox[0], bbox.bbox[1]]);
			map?.setBounds(myBounds, true, [20, 20, 20, 20]);
		}
	}

	const handleChangeLocationSet = async (locationSet: any) => {
		if (locationSet.id === context.currentMultipleLocations?.id) {
			dispatch({ type: AppAction.setCurrentMultipleLocations, payload: undefined });
			// 仅当线分析面板在打开地点集前处于可见状态时，关闭地点集才回到线分析面板
			if (context.lineAnalysisPanelVisible) {
				const currentShapeSource = (context.currentShape as any)?.source;
				const targetShapePanel =
					currentShapeSource === DrawShapeSource.POLYGON ||
					currentShapeSource === DrawShapeSource.SHAPE_POLYGON
						? RightPanel.Polygon
						: RightPanel.Line;
				dispatch({ type: AppAction.setRightPanel, payload: targetShapePanel });
			} else {
				dispatch({ type: AppAction.setRightPanel, payload: RightPanel.Empty });
			}
			dispatch({ type: AppAction.setArrowRight, payload: false });
		} else {
			if (context.currentMultipleLocationsAnalysed && context.currentMultipleLocationsAnalysed.id === locationSet.id) {
				dispatch({ type: AppAction.setMultipleLocationsAnalysed, payload: true });
			} else if (context.currentMultipleLocationsAnalysed) {
				dispatch({ type: AppAction.setMultipleLocationsAnalysed, payload: false });
			}
			if (locationSet.items === null || locationSet.items.length === 0) {
				await getItems(locationSet);
			} else {
				dispatch({ type: AppAction.setCurrentMultipleLocations, payload: locationSet });
				handleClickMyLocation(locationSet)
			}
			dispatch({ type: AppAction.setRightPanel, payload: RightPanel.Multiple });
		}
	};

	const importLocations = (): void => {
		dispatch({ type: AppAction.setImportMenu, payload: true });
	};

	const createSet = (): void => {
		dispatch({ type: AppAction.setCreateModal, payload: true });
	};

	const gotoPatchLocationSet = (locationSet: any): void => {
		dispatch({ type: AppAction.setCurrentMultipleLocations, payload: locationSet });
		dispatch({
			type: AppAction.setLeftPanel,
			payload: LeftPanel.EditLocationSet,
		});
	};

	const deleteSetModel = (locationSet: any): void => {
		dispatch({
			type: AppAction.setDeleteLocationSetModal,
			payload: locationSet,
		});
	};

	useEffect(() => {
		let arr = [...locationSets!];
		arr.sort((a: any, b: any) => {
			if (!a || !b) {
				return 0;
			}

			const aValue = a?.[active];
			const bValue = b?.[active];

			if (!aValue) {
				return direction[active] === "asc" ? -1 : 1;
			} else if (!bValue) {
				return direction[active] === "asc" ? 1 : -1;
			}

			if (direction[active] === "asc") {
				return String(aValue).localeCompare(String(bValue), 'zh-CN', { numeric: true })
			} else {
				return String(bValue).localeCompare(String(aValue), 'zh-CN', { numeric: true })
			}
		});

		setData([...arr]);
	}, [locationSets]);

	return (
		<div className={styles.sets}>
			<Paper
				sx={{ width: "100%", overflow: "hidden", height: "556px", position: "relative", boxShadow: "none", flex: 1 }}
				className={styles.table}
			>
				<TableContainer sx={{
					height: "100%",
					overflow: 'hidden',
					"& [data-overlayscrollbars-viewport]": {
						display: "flex",
						flexDirection: "column"
					}
				}}>
					<OverlayScrollbarsComponent
						style={{
							height: "100%"
						}}
						options={{
							overflow: { x: 'scroll', y: 'scroll' }, // 明确设置滚动方向
							scrollbars: {
								autoHide: 'leave',        // 鼠标移动/悬停时显示
								autoHideDelay: 0,        // 需要更“跟手”就设 0；默认有延迟
								theme: 'os-theme-dark'   // 可选：主题（或自定义 CSS）
							}
						}}
						defer     // 可选：延迟初始化，等内容渲染好（更稳）
					>
						<RadioGroup sx={{ flex: 1 }}>
							<Table
								stickyHeader
								aria-label="sticky  table"
								size="small"
								style={{
									tableLayout: "fixed",
									width: "100%",
									minWidth: "100%"
								}}
							>
								<TableHead>
									<TableRow className={styles.row}>
										<TableCell
											align="left"
											style={{ width: 45 }}
										>
											&nbsp;
										</TableCell>
										<TableCell
											align="center"
											style={{
												width: 65,
												whiteSpace: "nowrap"
											}}
										>
											{t("locations.table.type")}
										</TableCell>
										<TableCell
											align="left"
											style={{
												width: 170,
												whiteSpace: "nowrap"
											}}
										>
											<TableSortLabel
												active={active === "name"}
												onClick={() => handleSort("name")}
												direction={direction.name}
											>
												{t("locations.table.name")}
											</TableSortLabel>
										</TableCell>
										<TableCell
											style={{
												width: 120,
												textAlign: "right",
												whiteSpace: "nowrap"
											}}
										>
											<TableSortLabel
												active={active === "itemCount"}
												onClick={() => handleSort("itemCount")}
												direction={direction.itemCount}
											>
												<div className="flex flex-col">
													<span style={{ height: "15px", lineHeight: "normal" }}>{t("locations.table.locations")}</span>
													<span style={{ height: "15px", lineHeight: "normal" }}>{t("locations.table.selected")}</span>
												</div>
											</TableSortLabel>
										</TableCell>
										<TableCell
											align="center"
											style={{
												width: 150,
												whiteSpace: "nowrap"
											}}
										>
											<TableSortLabel
												active={active === "createTime"}
												onClick={() => handleSort("createTime")}
												direction={direction.createTime}
											>
												{t("locations.table.date")}
											</TableSortLabel>
										</TableCell>
										<TableCell style={{ padding: 0, width: 0 }}></TableCell>
									</TableRow>
								</TableHead>

								<TableBody>
									{data &&
										data.length > 0 &&
										data.map(
											(locationSet, index) =>
												(props.searchText === "" || locationSet.name.toLowerCase().includes(props.searchText.toLowerCase())) && (
													<TableRow
														key={index}
														selected={context.currentMultipleLocations && context.currentMultipleLocations.id === locationSet.id}
														onClick={() => openDataPoints(locationSet)}
														className={styles.row}
														sx={{
															"&.Mui-selected,&.Mui-selected:hover": {
																backgroundColor: "rgba(14, 138, 63, 0.08)!important",
															},
															"&.Mui-selected .icons-set": {
																background: "linear-gradient(90deg,rgba(236,246,240,0) 0,#ecf6f0 24px)!important",
															},
														}}
													>
														<TableCell
															style={{ padding: 0 }}
															align="left"
														>
															{isLoading === locationSet.id ? (
																<div className="p-[9px] w-[40px] h-[40px]">
																	<CircularProgress
																		color="success"
																		size={20}
																	/>
																</div>
															) : (
																<Radio
																	checked={context.currentMultipleLocations ? context.currentMultipleLocations.id === locationSet.id : false}
																	value={locationSet.id}
																	color="success"
																	onClick={(evt) => {
																		evt.stopPropagation();
																		handleChangeLocationSet(locationSet);
																	}}
																/>
															)}
														</TableCell>
														<TableCell align="center">
															<MapPinSvg className={styles.icon} />
														</TableCell>
														<TableCell align="left"
															style={{
																whiteSpace: "nowrap",
																overflow: "hidden",
																textOverflow: "ellipsis"
															}}
														>{locationSet.name}</TableCell>
														<TableCell
															align="right"
															style={{ paddingRight: "35px" }}
														>
															{getCheckedCounts(locationSet.items)}/{locationSet.itemCount}
														</TableCell>
														<TableCell align="center">{locationSet.createTime}</TableCell>
														<TableCell style={{ padding: 0 }}>
															<div className={`${styles.icons} icons-set`} onClick={(evt) => evt.stopPropagation()}>
																<Tooltip
																	title={<>
																		<div className={styles.locationSetDetails}>
																			{t("tooltip.locationSetDetails")}
																		</div>
																		<div className={styles.locationSetDetailsTitle}>
																			<span className={styles.locationSetDetailsTitleKey}>
																				{t("tooltip.uploadDate")}
																			</span>
																			<span className={styles.locationSetDetailsTitleValue}>
																				{locationSet.createTime}
																			</span>
																		</div>
																		<div className={styles.locationSetDetailsTitle}>
																			<span className={styles.locationSetDetailsTitleKey}>
																				{t("tooltip.comment")}
																			</span>
																			<span className={styles.locationSetDetailsTitleValue}>
																				{locationSet.comment}
																			</span>
																		</div>
																	</>}
																>
																	<IconButton style={{ width: "36px", height: "36px" }} >
																		<span className="material-icons">info</span>
																	</IconButton>

																</Tooltip>
																<Tooltip title={t("tooltip.edit")} >
																	<IconButton
																		style={{ width: "36px", height: "36px" }}
																		onClick={() => gotoPatchLocationSet(locationSet)}
																	>
																		<span className="material-icons">create</span>
																	</IconButton>
																</Tooltip>

																<Tooltip
																	title={t("tooltip.delete")}
																>
																	<IconButton
																		style={{ width: "36px", height: "36px" }}
																		onClick={() => deleteSetModel(locationSet)}
																	>
																		<span className="material-icons">delete</span>
																	</IconButton>
																</Tooltip>
															</div>
														</TableCell>
													</TableRow>
												)
										)}
								</TableBody>
							</Table>
						</RadioGroup>
						<div
							className={styles.pagination}
						>
							{t("locations.table.pagination")}
							{locationSets?.length} {t("locations.table.of")} {locationSets?.length}
						</div>
					</OverlayScrollbarsComponent>
				</TableContainer>
			</Paper >
			<div className={styles.footer}>
				<Stack
					direction="row"
					spacing={2}
				>
					<Button
						onClick={importLocations}
						variant="contained"
						color="success"
						style={{ width: "50%" }}
						endIcon={<ImportIcon style={{ width: "18px", height: "18px", fill: "white" }} />}
					>
						{t("buttons.import")}
					</Button>
					<Button
						onClick={createSet}
						variant="outlined"
						color="success"
						style={{ width: "50%" }}
						endIcon={<CreateIcon style={{ width: "18px", height: "18px", fill: "green" }} />}
					>
						{t("buttons.create")}
					</Button>
				</Stack>
			</div>
		</div >
	);
}

export default DataSets;

